import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Product } from '../db/Product.js';

// Matches dimension patterns like "15x3,5" or "15x15x3,5"
const DIMENSION_REGEX = /(\d+(?:[.,]\d+)?)\s*[xX×*]\s*(\d+(?:[.,]\d+)?)(?:\s*[xX×*]\s*(\d+(?:[.,]\d+)?))?/;
const toNum = (s: string) => parseFloat(s.replace(',', '.'));

export const searchProducts = registerQuery({
  operationName: 'searchProducts',
  type: () => [Product],
  params: [{ name: 'searchString', type: () => String }],
  resolve: (searchString: string) => async (gqlFields: BaseQLSelectionSet<Product>) => {
    const trimmed = searchString.trim();
    if (!trimmed) return [];

    const collection = await getRawCollection<Product>(Product._collectionName);
    const conditions: Record<string, any>[] = [];

    // Extract dimension pattern from input (e.g. "235 15x3,5" → dims "15x3,5", text "235")
    const dimMatch = trimmed.match(DIMENSION_REGEX);
    let textPart = trimmed;

    if (dimMatch) {
      textPart = trimmed.replace(dimMatch[0], '').trim();
      const filter: Record<string, any> = {};
      if (dimMatch[3]) {
        // AD1 x AD2 x Wand
        filter.outerDiameterOne = toNum(dimMatch[1]!);
        filter.outerDiameterTwo = toNum(dimMatch[2]!);
        filter.wallThickness = toNum(dimMatch[3]!);
      } else {
        // AD1 x Wand
        filter.outerDiameterOne = toNum(dimMatch[1]!);
        filter.wallThickness = toNum(dimMatch[2]!);
      }
      conditions.push(filter);
    }

    // Text tokens from remaining input
    const textTokens = textPart
      .split(/[\s,;]+/)
      .filter(t => t.length > 0);

    for (const token of textTokens) {
      const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = { $regex: escaped, $options: 'i' };
      conditions.push({
        $or: [{ materialNumber: regex }, { material: regex }, { productGroup: regex }, { deliveryCondition: regex }],
      });
    }

    if (conditions.length === 0) return [];

    const filter = conditions.length === 1 ? conditions[0]! : { $and: conditions };
    const results = await collection.find(filter).limit(50).toArray();
    return results.map(r => new Product(r));
  },
});
